import {
  CreateTopicDTO,
  GenerateQuestionsDTO,
  PatchQuestionDTO,
  PatchTopicDTO,
  PublishQuestionsDTO,
  QuestionDTO,
  QuestionEvaluationDTOType,
  QuestionQueryDTO,
  QuestionSubmissionDTOType,
  QuizDTO,
  ThumbsFeedbackDTO,
  TopicDTO,
  TopicListDTO,
  TopicQueryDTO,
  UpdateQuestionDTO,
} from "@onetutor/shared/dto";
import { QuestionQueryConfig } from "@pages/quiz/QuizSessionPage";
import { buildQueryString } from "../utils/api.util";
import { onetutor } from "./onetutor";

const quiz = onetutor.injectEndpoints({
  endpoints: (builder) => ({
    getTip: builder.query<
      string | null,
      { courseId: string; questionId: string }
    >({
      query: ({ courseId, questionId }) => ({
        url: `course/${courseId}/quiz/question/${questionId}/tip`,
        method: "GET",
        responseHandler: (response) => response.text(),
      }),
      providesTags: (_result, _error, args) => [
        { type: "tip", id: args.questionId },
      ],
    }),
    getTopics: builder.query<
      TopicListDTO,
      { courseId: string; queryArgs?: TopicQueryDTO }
    >({
      query: ({ courseId, queryArgs }) =>
        buildQueryString(`course/${courseId}/quiz/topics`, queryArgs),
      providesTags: (_result, _error, args) => [
        { type: "topic", id: args.courseId },
      ],
    }),
    getQuiz: builder.query<
      QuizDTO,
      { courseId: string; questionQueryConfig: QuestionQueryConfig }
    >({
      query: ({ courseId, questionQueryConfig }) =>
        buildQueryString(
          `course/${courseId}/quiz`,
          questionQueryConfig.queryParams,
        ),
      providesTags: ["question"],
    }),
    getQuestions: builder.query<
      QuizDTO,
      { courseId: string; queryParams: QuestionQueryDTO }
    >({
      query: ({ courseId, queryParams }) =>
        buildQueryString(`course/${courseId}/quiz/question`, queryParams),
      providesTags: (result, _error, _args) => {
        if (!result) {
          return [];
        }
        return result.questions.map((question) => ({
          type: "question" as const,
          id: question.id,
        }));
      },
    }),
    submitQuestion: builder.mutation<
      QuestionEvaluationDTOType,
      {
        courseId: string;
        questionId: string;
        submission: QuestionSubmissionDTOType;
      }
    >({
      query: ({ courseId, questionId, submission }) => ({
        url: `course/${courseId}/quiz/question/${questionId}/submit`,
        method: "POST",
        body: submission,
      }),
    }),
    createTopic: builder.mutation<
      TopicDTO,
      { courseId: string; createTopicData: CreateTopicDTO }
    >({
      query: ({ courseId, createTopicData }) => ({
        url: `course/${courseId}/quiz/topic`,
        method: "POST",
        body: createTopicData,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "topic", id: args.courseId },
      ],
    }),
    patchTopic: builder.mutation<
      TopicDTO,
      { courseId: string; topicId: string; patchTopicData: PatchTopicDTO }
    >({
      query: ({ courseId, topicId, patchTopicData }) => ({
        url: `course/${courseId}/quiz/topic/${topicId}`,
        method: "PATCH",
        body: patchTopicData,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "topic", id: args.courseId },
      ],
    }),
    deleteTopic: builder.mutation<void, { courseId: string; topicId: string }>({
      query: ({ courseId, topicId }) => ({
        url: `course/${courseId}/quiz/topic/${topicId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "topic", id: args.courseId },
        { type: "question" },
      ],
    }),
    generateQuestions: builder.mutation<
      QuizDTO,
      { courseId: string; generateQuestionsConfig: GenerateQuestionsDTO }
    >({
      query: ({ courseId, generateQuestionsConfig }) => ({
        url: `course/${courseId}/quiz/question/generate`,
        method: "POST",
        body: generateQuestionsConfig,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "question" },
        { type: "course", id: args.courseId },
        { type: "topic", id: args.courseId },
      ],
    }),
    deleteQuestion: builder.mutation<
      void,
      { courseId: string; questionId: string }
    >({
      query: ({ courseId, questionId }) => ({
        url: `course/${courseId}/quiz/question/${questionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "question", id: args.questionId },
        { type: "tip", id: args.questionId },
        { type: "topic", id: args.courseId },
      ],
    }),
    putQuestion: builder.mutation<
      QuestionDTO,
      { courseId: string; questionId: string; body: UpdateQuestionDTO }
    >({
      query: ({ courseId, questionId, body }) => ({
        url: `course/${courseId}/quiz/question/${questionId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "question", id: args.questionId },
        { type: "tip", id: args.questionId },
        { type: "topic", id: args.courseId },
      ],
    }),
    patchQuestion: builder.mutation<
      void,
      { courseId: string; questionId: string; body: PatchQuestionDTO }
    >({
      query: ({ courseId, questionId, body }) => ({
        url: `course/${courseId}/quiz/question/${questionId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "question", id: args.questionId },
        { type: "tip", id: args.questionId },
        { type: "topic", id: args.courseId },
      ],
    }),
    publishQuestions: builder.mutation<
      void,
      { courseId: string; body: PublishQuestionsDTO }
    >({
      query: ({ courseId, body }) => ({
        url: `course/${courseId}/quiz/question/publish`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, args) => [
        ...args.body.questionIds.map((id) => ({
          type: "question" as const,
          id,
        })),
        ...args.body.questionIds.map((id) => ({
          type: "tip" as const,
          id,
        })),
        { type: "topic", id: args.courseId },
      ],
    }),
    publishTopicQuestions: builder.mutation<
      void,
      { courseId: string; topicId: string }
    >({
      query: ({ courseId, topicId }) => ({
        url: `course/${courseId}/quiz/topic/${topicId}/questions/publish`,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "topic", id: args.courseId },
        { type: "question" },
      ],
    }),
    unpublishTopicQuestions: builder.mutation<
      void,
      { courseId: string; topicId: string }
    >({
      query: ({ courseId, topicId }) => ({
        url: `course/${courseId}/quiz/topic/${topicId}/questions/unpublish`,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "topic", id: args.courseId },
        { type: "question" },
      ],
    }),
    sendFeedback: builder.mutation<
      void,
      { courseId: string; questionId: string; feedback: ThumbsFeedbackDTO }
    >({
      query: ({ courseId, questionId, feedback }) => ({
        url: `course/${courseId}/quiz/question/${questionId}/feedback`,
        method: "POST",
        body: feedback,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTipQuery,
  useGetTopicsQuery,
  useGetQuizQuery,
  useGetQuestionsQuery,
  useSubmitQuestionMutation,
  useCreateTopicMutation,
  usePatchTopicMutation,
  useDeleteTopicMutation,
  useGenerateQuestionsMutation,
  useDeleteQuestionMutation,
  usePutQuestionMutation,
  usePublishQuestionsMutation,
  usePublishTopicQuestionsMutation,
  useUnpublishTopicQuestionsMutation,
  usePatchQuestionMutation,
  useSendFeedbackMutation,
} = quiz;
